// src/business/components/RequireBusiness.jsx
import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useBusinessAuth } from "../context/BusinessAuthContext";

/**
 * Prevent weird back-button access:
 * We store a small flag during logout so RequireBusiness
 * doesn't instantly bounce between states.
 */
const LOGOUT_FLAG = "business_logout_in_progress";

export default function RequireBusiness() {
  const { loading, isAuthenticated, businessId, syncSession } = useBusinessAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const redirectedRef = useRef(false);

  // Keep auth in sync on BFCache restore / tab visibility
  useEffect(() => {
    const check = () => {
      if (typeof syncSession === "function") syncSession();
    };

    const onPageShow = () => check();
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };

    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [syncSession]);

  // Redirect when unauthenticated
  useEffect(() => {
    if (loading) return;
    if (isAuthenticated && businessId) return;

    const logoutInProgress = sessionStorage.getItem(LOGOUT_FLAG) === "1";
    if (logoutInProgress) return;

    if (redirectedRef.current) return;
    redirectedRef.current = true;

    // Not authenticated OR authenticated without business → business login
    if (isAuthenticated && !businessId) {
      console.warn(`[RequireBusiness] redirect non-member to /business/login (no businessId)`);
    }
    navigate("/business/login", {
      replace: true,
      state: { from: location.pathname },
    });
  }, [loading, isAuthenticated, businessId, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">
        Loading business…
      </div>
    );
  }

  const logoutInProgress = sessionStorage.getItem(LOGOUT_FLAG) === "1";

  // Not authenticated → business login
  if (!isAuthenticated && !logoutInProgress) {
    return <Navigate to="/business/login" replace state={{ from: location.pathname }} />;
  }

  // Authenticated but no business membership → business login
  if (isAuthenticated && !businessId && !logoutInProgress) {
    console.warn(`[RequireBusiness] redirect non-member to /business/login (no businessId)`);
    return <Navigate to="/business/login" replace />;
  }

  return <Outlet />;
}

/**
 * Helper you can call inside topbar logout button:
 * - sets logout flag briefly to avoid UI weirdness
 */
export function markBusinessLogoutInProgress() {
  sessionStorage.setItem(LOGOUT_FLAG, "1");
  setTimeout(() => sessionStorage.removeItem(LOGOUT_FLAG), 900);
}
